import React, { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography
} from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import handleTicketAccessToUser from "../../utils/validations/handleTicketAccessToUser";
import TooltipContainer from "../TooltipContainer";
import useWhatsApps from "../../hooks/useWhatsApps";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
  dialogContent: {
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  contactCard: {
    borderRadius: "20px",
    padding: "10px",
    marginBottom: "5px",
  },

  contactCardActionArea: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    borderRadius: "20px",
    padding: "5px",
  },

  contactName: {
    color: theme.palette.secondary.ackCheck,
    fontSize: "16px",
  },

  contactNumber: {
    fontSize: "13px",
    color: "#959EAC",
  },
}));

const MultiVCardModal = ({ open, onClose, contactsList, enableStartChat, arePhoneNumbersValid }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { whatsApps: whatsAppsList } = useWhatsApps();

  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedContactName, setSelectedContactName] = useState("-");
  const [selectedWhatsappId, setSelectedWhatsappId] = useState(null);

  const [openPrivateChatConversationModal, setOpenPrivateChatConversationModal] = useState(false);

  const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (user.whatsapp?.id) setSelectedWhatsappId(user.whatsapp.id);
    else if (whatsAppsList.length === 1) setSelectedWhatsappId(whatsAppsList[0].id);
  }, [user, whatsAppsList]);

  

  //  **************
  //  ** Funtions **
  //  **************
  const handleOpenPrivateChatConversationModal = (selectedContactId, selectedContactName, selectedContactNumber, index) => {
    const isNumberValid = selectedContactId
      && selectedContactNumber
      && selectedContactNumber !== "-"
      && enableStartChat
      && arePhoneNumbersValid[index];

    if (!isNumberValid) {
      toast.info(i18n.t("multiVCardModal.toasts.noNumberProvided"));
      return;
    }

    setOpenPrivateChatConversationModal(true);
    setSelectedContactId(selectedContactId);
    setSelectedContactName(selectedContactName);
  };
  
  const handleClosePrivateChatConversationModal = () => {
    setOpenPrivateChatConversationModal(false);
    setSelectedContactId(null);
    setSelectedContactName("");
    setSelectedWhatsappId(null);
  };

  const handleClose = () => { 
    onClose();
    handleClosePrivateChatConversationModal();
    setSelectedContactId(null);
    setSelectedContactName("-");
  };

  const handleNewChat = async () => {
    if (!selectedWhatsappId) {
      toast.info(i18n.t("newTicketModal.toasts.whatsappNotSelected"));
      return;
    }

    if (!selectedContactId) {
      toast.info(i18n.t("multiVCardModal.toasts.noNumberProvided"));
      return;
    }

    await handleTicketAccessToUser(
      selectedContactId,
      user.id,
      user.profile,
      "open",
      true,
      selectedWhatsappId,
      null,
      (ticketId, ticketStatus) => {
        localStorage.setItem("tabOpenTickets", ticketStatus);
        handleClose();
        DefaultFunctionsUtils.redirectToTicketsPage(history, `/${ticketId}`);
        setShouldSyncRouteOnMenu(true);
      }
    );
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={`${i18n.t("contacts.confirmationModal.openPrivateChat.title")} ${selectedContactName}?`}
        open={openPrivateChatConversationModal}
        onClose={handleClosePrivateChatConversationModal}
        onConfirm={async () => await handleNewChat()}
      >
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{i18n.t("newTicketModal.connection")}</InputLabel>
          <Select
            value={selectedWhatsappId}
            onChange={(e) => setSelectedWhatsappId(e.target.value)}
            label={i18n.t("newTicketModal.connection")}
            fullWidth
          >
            <MenuItem value={""}>&nbsp;</MenuItem>
            {whatsAppsList.map((whatsapp) => (
              <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </ConfirmationModal>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" scroll="paper" fullWidth>
        <DialogTitle id="form-dialog-title">
          <span>{i18n.t("multiVCardModal.title.up")}</span>
        </DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
          {contactsList.map((contactItem, contactIndex) => (
            <Card
              className={classes.contactCard}
              onClick={() => handleOpenPrivateChatConversationModal(contactItem.id, contactItem.name, contactItem.number, contactIndex)}
            >
              <CardActionArea
                className={classes.contactCardActionArea}
                disabled={!arePhoneNumbersValid[contactIndex] || !enableStartChat}
              >
                <Avatar alt={contactItem.name} src={contactItem.profilePicUrl} className={classes.contactAvatar} />

                <CardContent>
                  <Typography className={classes.contactName} gutterBottom component="h3">
                    {contactItem.name}

                    {!arePhoneNumbersValid[contactIndex] && (
                      <TooltipContainer i18nMessage={"backendErrors.ERR_NUMBER_NOT_IN_WHATSAPP"} />
                    )}
                  </Typography>

                  <Typography className={classes.contactNumber} gutterBottom component="h3">{contactItem.number}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MultiVCardModal;